import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Box, Typography, Button, Icon } from "@mui/material";
import { BusinessList } from "../components/BusinessList/BusinessList";
import { BusinessFilters } from "../components/BusinessFilters/BusinessFilters";
import { useSuperAdminContext } from "../providers/SuperAdminProvider";
import { BusinessDetails } from "../components/BusinessDetails/BusinessDetails";
import { BusinessForm } from "../components/BusinessForm/BusinessForm";
import { superAdminService } from "../../../services/super-admin.service";
import { toast } from "react-toastify";

interface BusinessFiltersState {
  page: number;
  rowsPerPage: number;
  searchTerm: string;
  statusFilter: string;
}

export const BusinessManagement: React.FC = () => {
  // Group related state into an object
  const [filters, setFilters] = useState<BusinessFiltersState>({
    page: 0,
    rowsPerPage: 10,
    searchTerm: "",
    statusFilter: "all",
  });
  const [selectedBusinessId, setSelectedBusinessId] = useState<string | null>(
    null
  );
  const [isBusinessFormOpen, setIsBusinessFormOpen] = useState(false);

  const {
    businesses,
    totalBusinesses,
    isLoading,
    fetchBusinesses,
    toggleBusinessStatus,
    businessStats,
  } = useSuperAdminContext();

  // Memoize filter handlers
  const handlePageChange = useCallback((newPage: number) => {
    setFilters((prev) => ({ ...prev, page: newPage }));
  }, []);

  const handleRowsPerPageChange = useCallback((newRowsPerPage: number) => {
    setFilters((prev) => ({ ...prev, rowsPerPage: newRowsPerPage, page: 0 }));
  }, []);

  const handleSearchChange = useCallback((newSearchTerm: string) => {
    setFilters((prev) => ({ ...prev, searchTerm: newSearchTerm, page: 0 }));
  }, []);

  const handleStatusChange = useCallback((newStatus: string) => {
    setFilters((prev) => ({ ...prev, statusFilter: newStatus, page: 0 }));
  }, []);

  // Fetch businesses when filters change
  useEffect(() => {
    const { page, rowsPerPage, searchTerm, statusFilter } = filters;
    fetchBusinesses({
      page,
      limit: rowsPerPage,
      search: searchTerm,
      status: statusFilter,
    });
  }, [
    filters.searchTerm,
    filters.page,
    filters.rowsPerPage,
    filters.statusFilter,
    fetchBusinesses,
    filters,
  ]);

  // Memoize business selection handlers
  const handleViewDetails = useCallback((businessId: string) => {
    setSelectedBusinessId(businessId);
  }, []);

  const handleCloseDetails = useCallback(() => {
    setSelectedBusinessId(null);
  }, []);

  // Handle business creation
  const handleCreateBusiness = useCallback(
    async (data: any) => {
      try {
        await superAdminService.createBusiness(data);
        toast.success("Business created successfully");
        fetchBusinesses(filters);
        setIsBusinessFormOpen(false);
      } catch (error) {
        console.error("Error creating business:", error);
        toast.error("Failed to create business");
      }
    },
    [filters, fetchBusinesses]
  );

  // Memoize selected business data
  const selectedBusinessData = useMemo(() => {
    if (!selectedBusinessId) return null;
    return (
      businesses.find((business) => business.id === selectedBusinessId) ?? null
    );
  }, [businesses, selectedBusinessId]);

  // Memoize selected business stats
  const selectedBusinessStats = useMemo(() => {
    if (!selectedBusinessId) return null;
    return businessStats[selectedBusinessId] ?? null;
  }, [businessStats, selectedBusinessId]);

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4">Business Management</Typography>
        <Button
          variant="contained"
          startIcon={<Icon>add</Icon>}
          disabled={isLoading}
          onClick={() => setIsBusinessFormOpen(true)}
        >
          Add Business
        </Button>
      </Box>

      <BusinessFilters
        searchTerm={filters.searchTerm}
        status={filters.statusFilter}
        onSearchChange={handleSearchChange}
        onStatusChange={handleStatusChange}
        disabled={isLoading}
      />

      <BusinessList
        businesses={businesses}
        page={filters.page}
        totalCount={totalBusinesses}
        rowsPerPage={filters.rowsPerPage}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        onViewDetails={handleViewDetails}
        onToggleStatus={toggleBusinessStatus}
        isLoading={isLoading}
      />

      <BusinessDetails
        open={!!selectedBusinessId}
        onClose={handleCloseDetails}
        business={selectedBusinessData}
        stats={selectedBusinessStats}
        isLoading={isLoading}
      />

      <BusinessForm
        open={isBusinessFormOpen}
        onClose={() => setIsBusinessFormOpen(false)}
        onSubmit={handleCreateBusiness}
      />
    </Box>
  );
};
